// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import clsx from "clsx";
import { FC, useEffect, useId, useMemo } from "react";

import { PlannerInsert } from "../section/planner/PlannerInsert";

import { SessionInProgressButton } from "./SessionInProgressButton";

import { serverGetHomePlannerPages, serverGetPageAssessmentStatuses } from "api/api-server-data";
import { CoreUserRoleType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookCourseIcon, ChevronDownIcon, ChevronUpIcon, EyeNotVisibleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectCoursesForCurrentUser, selectTerms } from "store/selectors";
import { sortByTermYearRegex } from "store/selectors/sorting";
import { computedStateActions } from "store/slices/computed";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { HomeRoleType, courseHomeUrl } from "utils/urls";

const PARTICIPANT_ROLES = ["student", "instructor"];

export const HomeCourseList: FC<{ role: HomeRoleType }> = ({ role }) => {
  const id = useId();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(selectTerms);
  const { canAuthorCourse } = useCapabilities();
  const coursesForSelectedRole = useAppSelector((s) => selectCoursesForCurrentUser(s, role));
  const { isActive, isArchived, sortByActiveTitles } = useIsArchived();
  const isSmallScreen = !useIsMdOrLarger();

  useEffect(() => {
    const loadPlannerData = async () => {
      const pages = await serverGetHomePlannerPages(role as CoreUserRoleType);
      if (pages?.length) {
        dispatch(slices.pages.actions.updateMany(pages));

        const pageIds = pages.map((p) => p.id);

        storeApi.page_access_data.list({ page_id__in: pageIds });
        const statuses = await serverGetPageAssessmentStatuses({ pageIds });

        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      }
    };

    if (PARTICIPANT_ROLES.includes(role)) {
      loadPlannerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const termsWithCourses = useMemo(
    () =>
      terms.toSorted(sortByTermYearRegex).map((term) => {
        const coursesForTerm = coursesForSelectedRole
          .filter((course) => course.term_id === term.id && (canAuthorCourse || isActive(course)))
          .toSorted(sortByActiveTitles);
        return {
          term,
          courses: coursesForTerm,
        };
      }),
    [coursesForSelectedRole, canAuthorCourse, terms, isActive, sortByActiveTitles],
  ).filter((term) => term.courses.length > 0);

  if (coursesForSelectedRole.length === 0) {
    return <Banner kind="info">{t("home.no_courses_for_role")}</Banner>;
  }

  return (
    <div className="flex h-full flex-col-reverse gap-6 overflow-y-auto md:flex-row md:items-start md:gap-10">
      <div className="flex flex-1 flex-col gap-8">
        {termsWithCourses.map((termWithCourses) => (
          <Disclosure key={termWithCourses.term.id} defaultOpen>
            {({ open }) => {
              const titleId = `${id}-${termWithCourses.term.id}`;
              return (
                <div>
                  <DisclosureButton
                    className="flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium text-black-tint-10"
                    title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                  >
                    <span id={titleId}>{termWithCourses.term.title}</span>
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </DisclosureButton>

                  <DisclosurePanel className="flex flex-col gap-2">
                    <ol aria-labelledby={titleId} className="flex flex-col">
                      {termWithCourses.courses.map((course) => (
                        <ListItem
                          className="w-full justify-between first:border-t-0"
                          key={course.id}
                          kind="primary"
                          to={courseHomeUrl(course.id)}
                        >
                          <div className="flex min-w-0 flex-auto">
                            <IconText
                              className={clsx("truncate", {
                                "text-black-tint-55": isArchived(course),
                              })}
                              iconStart={
                                isArchived(course) ? <EyeNotVisibleIcon /> : <BookCourseIcon />
                              }
                              text={<span className="min-w-12 truncate">{course.title}</span>}
                            />
                          </div>
                          <SessionInProgressButton courseId={course.id} />
                        </ListItem>
                      ))}
                    </ol>
                  </DisclosurePanel>
                </div>
              );
            }}
          </Disclosure>
        ))}
      </div>

      {PARTICIPANT_ROLES.includes(role) && (
        <div className={clsx("flex-shrink-0", !isSmallScreen && "mb-6")}>
          <PlannerInsert role={role as CoreUserRoleType} startOpen={!isSmallScreen} />
        </div>
      )}
    </div>
  );
};
